import { useQuery } from "@apollo/client/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

import { constantsData, selectionsData } from "@/data";
import { getTerritoriesListQuery } from "@/graph";
import { openAlert, setSelection } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useTerritoriesList = (paginated = true, hasAutoCompleteMenu = false, searchValue, isDrawer = false) => {
    const [fetched, setFetched] = useState(false);

    const [params] = useSearchParams();

    const pageParam = isDrawer ? 1 : parseInt(params.get("page")) || 1;

    const limitParam = isDrawer ? 25 : parseInt(params.get("limit")) || 25;

    const [page, setPage] = useState(pageParam);

    const [limit, setLimit] = useState(limitParam);

    const dispatchedDataRef = useRef(null);

    const dispatch = useDispatch();

    const selections = useSelector((state) => state.selectionsReducer);

    const { isAuthenticated } = useAuth0();

    const { t } = useTranslation();

    const { territories: territoriesKey } = selectionsData.keys;

    const { data, error, loading, refetch } = useQuery(getTerritoriesListQuery, {
        skip: !isAuthenticated,
        variables: {
            orderBy: { name: "asc" },
            ...(paginated && {
                limit,
                page: page - 1,
            }),
            ...(searchValue && {
                where: {
                    name: { contains: searchValue },
                },
            }),
        },
    });

    if (data?.listTerritories && dispatchedDataRef.current !== data) {
        dispatchedDataRef.current = data;
        if (paginated && !hasAutoCompleteMenu) setFetched(true);
        else {
            const fetchedList = data.listTerritories.posts?.map(({ id, name }) => ({
                label: name,
                value: id,
            }));

            dispatch(
                setSelection({
                    [territoriesKey]:
                        selections?.[territoriesKey]?.length > 0 &&
                        hasAutoCompleteMenu &&
                        data?.listTerritories?.meta?.currentPage > 0
                            ? selections?.[territoriesKey].concat(fetchedList)
                            : fetchedList,
                }),
            );

            setFetched(true);
        }
    }

    useEffect(
        () => {
            if (error?.message === constantsData.failedToFetch && !loading) {
                displayErrorHandler(t("messages.networkFailure"), dispatch, openAlert);
            }
        },
        [error, data], // eslint-disable-line
    );

    return {
        count: data?.listTerritories?.meta?.nodeCount,
        data: data?.listTerritories?.posts,
        fetched,
        limit,
        loading,
        page: data?.listTerritories?.meta?.currentPage,
        refetch,
        searchKey: "name",
        setFetched,
        setLimit,
        setPage,
    };
};

export default useTerritoriesList;
